<template>
  <div
    ref="contentItem"
    class="new-content-item d-flex cursor-pointer"
    :class="{ 'bg-light': isArticleSelected }"
    @click="selectArticle()"
  >
    <div
      class="content-item-image-container"
    >
      <span
        v-if="contentItemImageUrl === 'loading'"
        :class="['content-item-image ', { 'loader': contentItemImageUrl === 'loading' }]"
      />
      <img
        v-else
        class="content-item-image"
        :src="contentItemImageUrl"
        data-test="content-item-image"
      >
    </div>
    <div
      class="d-flex flex-column content-item-info justify-content-between w-100"
    >
      <p
        class="font-weight-bold content-item-title text-wrap word-break-all"
      >
        {{ contentItemTitle }}
      </p>
      <ContentItemActions
        v-if="!isCampaignEnded && article.id"
        class="content-item-actions"
        :content-item="article"
      />
      <div>
        <p
          class="small content-item-state"
        >
          <span
            v-if="isViewType('Editor')"
            data-test="content-item-control"
          >
            <i
              :class="['far', pausedState ? 'fa-play' : 'fa-pause']"
              @click="updateContentItemState()"
            />
          </span>
          State: {{ contentItemState }}
        </p>
        <div class="update-date-block">
          <p
            class="small mt-auto content-item-update-date"
          >
            Edited: {{ contentItemUpdateDate }}
          </p>
          <div
            class="flex-shrink-0"
          >
            <span
              v-if="hasComments"
            >
              <i class="fal fa-comment-alt" />
              {{ commentsCount }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import suppressibleBox from '@/core/mixins/suppressibleBox';
import ContentItemActions from '@/modules/content/components/Articles/ContentItemActions.vue';

export default {
  name: 'ContentItemCard',
  components: { ContentItemActions },
  mixins: [
    suppressibleBox,
  ],
  props: {
    article: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: () => 0,
    },
  },
  data() {
    return {

    };
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_SELECTED_ARTICLE_UUID,
      contentTypes.UPDATE_CONTENT_ITEM,
      contentTypes.FETCH_ARTICLES,
    ]),
    ...mapMutations('content', [
      contentTypes.SET_CONTENT_TOTAL_PUBLISHED_ITEMS,
    ]),
    selectArticle() {
      this.SET_SELECTED_ARTICLE_UUID(this.article.encodedExternalId);
    },
    toggleContentState(state) {
      const newPublishedCount = this.getNewPublishedCount(this.article.state, state);
      const payload = {
        advertisersId: this.selectedAdvertiser.id,
        feedId: this.feedId,
        id: this.article.id,
        body: { state },
      };
      this.UPDATE_CONTENT_ITEM(payload)
        .then(() => {
          this.SET_CONTENT_TOTAL_PUBLISHED_ITEMS(newPublishedCount);
          this.FETCH_ARTICLES();
        });
    },
    getNewPublishedCount(currentState, newState) {
      let newCount = this.totalPublished;
      if (currentState === 'PAUSED' && newState === 'PUBLISHED') {
        newCount++;
      } else if (currentState === 'PUBLISHED' && newState === 'PAUSED') {
        newCount--;
      }
      return newCount;
    },
    updateContentItemState() {
      if (this.isReadOnly()) return;
      let newState = '';
      let messageText = '';
      let suppressKey = null;
      switch (this.article.state) {
        case 'PUBLISHED':
          newState = 'PAUSED';
          break;
        case 'DRAFT':
          newState = 'APPROVAL';
          messageText = 'All ad content goes through an approvals process to make sure it\'s safe and appropriate for users. Approvals can take up to 2 working days.';
          suppressKey = 'suppressSendForApprovalDialog';
          break;
        case 'PAUSED':
          newState = 'PUBLISHED';
          messageText = 'This content will now go live.';
          suppressKey = 'suppressPublishPausedItemDialog';
          break;
        case 'APPROVAL':
          newState = 'DRAFT';
          break;
        default:
          newState = 'DRAFT';
      }
      if (this.totalPublished === 1 && this.state === 'PUBLISHED') {
        this.boxOk(
          'Warning',
          'You have just paused the last published item. This campaign will not serve any ads until you publish some content',
          'suppressPauseLastPublishedItemsDialog',
        ).then(this.toggleContentState(newState));
      } else if (messageText !== '') {
        this.boxOk('Information', messageText, suppressKey)
          .then(this.toggleContentState(newState));
      } else {
        this.toggleContentState(newState);
      }
    },
  },
  computed: {
    ...mapState('content', [
      'selectedArticleUuid',
      'newContentItems',
      'commentsCounts',
      'currentComments',
      'selectedCreativeId',
      'feedId',
      'totalPublished',
    ]),
    ...mapState('campaigns', [
      'campaign',
      'selectedAdvertiser',
      'viewType',
    ]),
    ...mapGetters('campaigns', {
      isViewType: campaignTypes.GET_IS_VIEW_TYPE,
      isReadOnly: campaignTypes.GET_IS_READ_ONLY,
      isSelfCampaignAccessedByAdmin: campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
      isCampaignEnded: campaignTypes.GET_IS_CAMPAIGN_ENDED,
    }),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEM_DISPLAY_PROPERTIES,
    ]),
    isArticleSelected() {
      if (!this.selectedArticleUuid && this.index === 0) this.selectArticle();
      return this.selectedArticleUuid === this.article.encodedExternalId;
    },
    contentItemTitle() {
      const articleDisplayProperty = this.GET_CONTENT_ITEM_DISPLAY_PROPERTIES(this.article.encodedExternalId, this.selectedCreativeId);
      return articleDisplayProperty ? articleDisplayProperty.title : this.article.title;
    },
    contentItemState() {
      return this.article.state;
    },
    pausedState() {
      return (this.article.state === 'DRAFT' || this.article.state === 'PAUSED');
    },
    contentItemUpdateDate() {
      if (!this.article.updatedDate) return '';
      const date = new Date(this.article.updatedDate);
      return `${date.getDate()}/${(date.getMonth() + 1)}/${date.getFullYear()}`;
    },
    hasComments() {
      return this.commentsCounts.hasOwnProperty(this.article.encodedExternalId);
    },
    commentsCount() {
      return this.commentsCounts[this.article.encodedExternalId].count;
    },
    contentItemImageUrl() {
      const articleDisplayProperty = this.GET_CONTENT_ITEM_DISPLAY_PROPERTIES(this.article.encodedExternalId, this.selectedCreativeId);
      return articleDisplayProperty ? articleDisplayProperty.imageFileName : (this.article.imageUrl || '/images/image-not-found.png');
    },
  },
};
</script>
<style lang="scss" scoped>
* {
  margin: 0;
  padding: 0;
}
.new-content-item {
  height: 136px;
  padding: 16px;
  border-top: 1px solid hsla(191, 67%, 14%, 0.12);
}
.content-item-image-container {
  flex-shrink: 0;
  width: 125px;
  height: 104px;
}
.content-item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.content-item-info {
  position: relative;
  padding-left: 16px;
}
.content-item-actions {
  position: absolute;
  top: -10px;
  right: -20px;
}
.content-item-title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  margin-right: 5px;
}
.update-date-block {
  display: flex;
  justify-content: space-between;
}
.content-item-update-date {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  overflow: hidden;
  word-break: break-all;
}
.new-content-item-container {
  position: relative;
}
</style>
